import { CustomerType } from '@integration/base-models';
import type { ProgressiveFormStateImplementor } from '@innogy/progressive-ngrx-forms';
import {
  activateFormStep,
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
  onProgressiveNGRXFormsAction,
  SubmitFormStepAction,
} from '@innogy/progressive-ngrx-forms';
import type { InnogyComponentRendering } from '@core/jss-models';
import type { Action } from '@ngrx/store';
import { on } from '@ngrx/store';
import { updateGroup, validate } from 'ngrx-forms';
import { required } from 'ngrx-forms/validation';
import { validateSequentialIf } from '@innogy/utils-deprecated';

import { ORDER_CROSS_SELL_FORM_ID, orderCrossSellEnabled } from '../cross-sell';
import { ORDER_ADDRESS_FORM_ID } from '../address';
import { addOrderRenderingAction } from '../order.actions';
import type { OrderState } from '../order.state';

/**
 * DEBT: same reason as for cross-sell
 * @see '../cross-sell/order-cross-sell.reducer.ts'
 */
const stepActivationReducer = onProgressiveNGRXFormsAction(
  SubmitFormStepAction,
  (state: OrderCustomerTypeState, action) => {
    if (
      action.stepId !== state.previousStepId ||
      state.progressiveForm.isSubmitted
    ) {
      return state;
    }
    return {
      ...state,
      progressiveForm: activateFormStep(state.progressiveForm),
    };
  }
);

export const ORDER_CUSTOMER_TYPE_FORM_ID: keyof OrderState =
  'orderCustomerType';

export const ORDER_CUSTOMER_TYPE_OTHER_SEGMENT = 'sohoOrSme';

export interface OrderCustomerTypeFormValues {
  segmentQuestion:
    | CustomerType.HOUSEHOLD
    | typeof ORDER_CUSTOMER_TYPE_OTHER_SEGMENT
    | undefined;
  // Omit<CustomerType, CustomerType.HOUSEHOLD>
  // does not create the type that we can use in the control field, so we explicitly define the list of allowed types
  companySize:
    | CustomerType.MICRO
    | CustomerType.SMALL
    | CustomerType.MEDIUM
    | CustomerType.LARGE
    | undefined;
}

export const initialCustomerTypeProgressiveFormState =
  createProgressiveFormGroupState<OrderCustomerTypeFormValues>(
    ORDER_CUSTOMER_TYPE_FORM_ID,
    {
      segmentQuestion: undefined,
      companySize: undefined,
    }
  );

export const initialOrderCustomerTypeState: OrderCustomerTypeState = {
  progressiveForm: initialCustomerTypeProgressiveFormState,
  rendering: undefined,
  enabled: false,
  previousStepId: ORDER_ADDRESS_FORM_ID,
};

export interface OrderCustomerTypeState
  extends ProgressiveFormStateImplementor<OrderCustomerTypeFormValues> {
  rendering?: InnogyComponentRendering;
  previousStepId: keyof OrderState;
  enabled: boolean;
}

const validateForm = (state: OrderCustomerTypeState) =>
  updateGroup<OrderCustomerTypeFormValues>({
    segmentQuestion: validate(required),
    companySize: validateSequentialIf(
      state.progressiveForm.formState.value.segmentQuestion !==
        CustomerType.HOUSEHOLD
    )(required),
  })(state.progressiveForm.formState);

const _reducer = createProgressiveNgrxFormReducerWithFormStateUpdate(
  initialOrderCustomerTypeState,
  validateForm,
  on(addOrderRenderingAction, (state, action) => ({
    ...state,
    rendering: action.rendering,
  })),
  on(orderCrossSellEnabled, (state, action) => {
    // If the order cross-sell step is enabled, this order step is activated after that step id instead
    // we can ignore action.isCustomerTypeEnabled, because if that would be false, this reducer would not be called
    if (action.enabled) {
      return { ...state, previousStepId: ORDER_CROSS_SELL_FORM_ID };
    } else {
      return { ...state, previousStepId: ORDER_ADDRESS_FORM_ID };
    }
  }),
  stepActivationReducer
);

export function orderCustomerTypeReducer(
  state: OrderCustomerTypeState = initialOrderCustomerTypeState,
  action: Action
): OrderCustomerTypeState {
  return _reducer(state, action);
}
